import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { ANLoadingScreen } from "@answersnow/pkg-theme";
import * as selectors from "selectors";
import { useCustomerDetails } from "hooks/useCustomerDetails";
import { useUpdateClientWorkflowStatus } from "hooks/api/client/useUpdateClientWorkflowStatus";
import { useGetScheduledDeactivation } from "hooks/api/client/useScheduledDeactivation";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { ClientAccountSettings, AccountSettingsFormValues } from "./interface";
import { UserPermissions } from "../../../../types/permissions";

export const AccountSettings: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const userPermissions = useSelector(selectors.getUserPermissionsList) as UserPermissions | null;

  const [editMode, setEditMode] = useState(false);
  const [isFutureDeactivationEdit, setIsFutureDeactivationEdit] = useState(false);
  const [isConfirmingScheduledDeactivation, setIsConfirmingScheduledDeactivation] = useState(false);

  const {
    data: customerDetails,
    isLoading: customerLoading,
    refetch: refetchCustomerDetails,
  } = useCustomerDetails(clientId);

  // Fetch scheduled deactivation data
  const { data: scheduledDeactivationData, refetch: refetchScheduledDeactivation } =
    useGetScheduledDeactivation({
      clientId,
      enabled: !!clientId,
    });

  const { updateWorkflowStatus, isUpdating } = useUpdateClientWorkflowStatus({
    onSuccess: async () => {
      await refetchCustomerDetails();
      await refetchScheduledDeactivation();
      setEditMode(false);
    },
  });

  // The useCustomerDetails hook returns the customer object directly (not wrapped in .details)
  const workflowStatus = customerDetails?.workflow_status || "";
  // Use ?? instead of || to preserve false values (deactivated clients)
  // Only defaults to true if is_active is null or undefined
  const isActive = customerDetails?.is_active ?? true;
  const childName = customerDetails?.name || "";
  const childLastName = customerDetails?.child_last_name || "";

  const accountSettings: ClientAccountSettings = {
    workflowStatus,
    isActive,
    scheduledDeactivationData: scheduledDeactivationData || undefined,
  };

  // ClientActivation expects customerDetails in the old format with .details property
  // Wrap the data to maintain compatibility with the shared widget
  const customerDetailsFormatted = customerDetails ? { details: customerDetails } : null;

  const [formValues, setFormValues] = useState<AccountSettingsFormValues>({
    workflowStatus: "",
  });

  const [initialWorkflowStatus, setInitialWorkflowStatus] = useState<string>("");

  useEffect(() => {
    if (workflowStatus) {
      setFormValues({
        workflowStatus,
      });
      setInitialWorkflowStatus(workflowStatus);
    }
  }, [workflowStatus]);

  const handleChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues({ ...formValues, [field]: event.target.value });
    };

  const handleSave = () => {
    if (!clientId) return;

    // Only update if the workflow status has changed
    if (formValues.workflowStatus !== initialWorkflowStatus) {
      updateWorkflowStatus({
        clientId,
        status: formValues.workflowStatus,
      });
    } else {
      // If no changes were made, just exit edit mode
      setEditMode(false);
    }
  };

  const handleCancel = () => {
    if (workflowStatus) {
      setFormValues({
        workflowStatus,
      });
    }
    setEditMode(false);
    setIsFutureDeactivationEdit(false);
    setIsConfirmingScheduledDeactivation(false);
  };

  const handleDeactivationSuccess = async () => {
    await refetchCustomerDetails();
    await refetchScheduledDeactivation();
    setEditMode(false);
    setIsFutureDeactivationEdit(false);
    setIsConfirmingScheduledDeactivation(false);
  };

  const handleEditFutureDeactivation = () => {
    setIsFutureDeactivationEdit(true);
    if (!editMode) {
      setEditMode(true);
    }
  };

  const handleConfirmScheduledDeactivation = () => {
    setIsConfirmingScheduledDeactivation(true);
    if (!editMode) {
      setEditMode(true);
    }
  };

  const handleClearDeactivationInfo = () => {
    setIsFutureDeactivationEdit(false);
    setIsConfirmingScheduledDeactivation(false);
  };

  const canEdit = Boolean(userPermissions?.override_client_workflow_state);
  const canDeactivate = Boolean(userPermissions?.disable_client);

  const hasScheduledCall = Boolean(
    customerDetails?.upcomingVideoCalls && customerDetails.upcomingVideoCalls.length > 0
  );

  if (customerLoading || isUpdating) {
    return <ANLoadingScreen />;
  }

  if (!customerDetails) {
    return (
      <Box sx={{ p: 5 }}>
        <Typography>No client information found</Typography>
      </Box>
    );
  }

  return (
    <SectionWrapper
      title="Account Settings"
      description="Manage client account status and activation"
      editMode={editMode}
      onEdit={canEdit ? () => setEditMode(true) : undefined}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={formValues.workflowStatus === initialWorkflowStatus}
    >
      {editMode ? (
        <EditFields
          formValues={formValues}
          accountSettings={accountSettings}
          handleChange={handleChange}
          userPermissions={userPermissions}
          clientId={clientId}
          customerDetails={customerDetailsFormatted}
          hasScheduledCall={hasScheduledCall}
          childName={childName}
          childLastName={childLastName}
          onDeactivationSuccess={handleDeactivationSuccess}
          onClearDeactivationInfo={handleClearDeactivationInfo}
          isFutureDeactivationEdit={isFutureDeactivationEdit}
          isConfirmingScheduledDeactivation={isConfirmingScheduledDeactivation}
          onEditFutureDeactivation={handleEditFutureDeactivation}
          onConfirmScheduledDeactivation={handleConfirmScheduledDeactivation}
        />
      ) : (
        <ViewFields
          accountSettings={accountSettings}
          onEditFutureDeactivation={handleEditFutureDeactivation}
          onConfirmScheduledDeactivation={handleConfirmScheduledDeactivation}
          canDeactivate={canDeactivate}
        />
      )}
    </SectionWrapper>
  );
};

export default AccountSettings;
